import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Modal, Select, message, Tag, Popconfirm, DatePicker, Radio } from 'antd';
import { PlusOutlined, SwapOutlined, PercentageOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import api from '../../api/axios';
import { getTerms, getClassrooms, getCampaigns } from '../../api/settings';
import ClassroomLoadPanel from './ClassroomLoadPanel';

dayjs.extend(isoWeek);

const { Option } = Select;

const DAY_NAMES = ['', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];

/**
 * Bir derslikte GERÇEKTEN dersi olan saatleri sunucudan çeker.
 *
 * Eskiden saatler dönemin `first_slot_time`/`last_slot_time` alanından
 * üretiliyordu. Toplu ders üretimi gün bazlı hale gelince (hafta içi
 * 14:30–20:30, cumartesi 10:30–20:30) o tek aralık gerçeği yansıtmaz oldu:
 * Pazartesi için 10:30 gösteriliyor, seçilince o saatte ders olmadığı hâlde
 * yeni ders yaratılıyordu.
 *
 * Kaynak bilerek dönemin `session_config`'i DEĞİL, gerçek ders seansları —
 * üretim hiç çalışmamış, tatiller düşülmüş ya da dersler silinmiş olabilir.
 */
const fetchSlots = async ({ classroomId, termId, dayOfWeek, date, startDate }) => {
  if (!classroomId || !termId || (!dayOfWeek && !date)) return { slots: [], total_weeks: 0 };

  const { data } = await api.get('/schedule/available-slots', {
    params: {
      classroom_id: classroomId,
      term_id:      termId,
      // isoWeekday (1=Pazartesi … 7=Pazar) — uç da bunu bekliyor.
      day_of_week:  date ? undefined : dayOfWeek,
      date:         date || undefined,
      start_date:   startDate || undefined,
    },
  });

  return data;
};

const TYPE_LABELS = { normal: 'Normal', regular: 'Normal', trial: 'Deneme', makeup: 'Telafi', makeup_uncontrolled: 'Kontrolsüz Telafi', one_time: 'Tek Seferlik' };
const TYPE_COLORS = { normal: 'blue', regular: 'blue', trial: 'gold', makeup: 'orange', makeup_uncontrolled: 'volcano', one_time: 'geekblue' };

/**
 * Kaydin "ne zaman" etiketi.
 *
 * Yalnizca 'regular' her hafta tekrar eder; diger tum tipler (deneme,
 * telafi, kontrolsuz telafi, tek seferlik) TEK bir derse karsilik gelir.
 * Bunlarda sadece gun adi yazmak yetmiyordu - "Çarşamba 13:30" deyip
 * hangi carsamba oldugunu soylememek kullaniciyi takvime bakmak zorunda
 * birakiyordu. Tek seferlik tiplerde gun adinin yanina gercek tarih de
 * yaziliyor.
 */
const slotWhenLabel = (slot) => {
  const day  = DAY_NAMES[slot.day_of_week];
  const date = slot.lesson_sessions?.[0]?.date;

  if (slot.type !== 'regular' && date) {
    return `${day} ${dayjs(date).format('DD.MM.YYYY')}`;
  }
  return day;
};

const StudentEnrollmentsTab = ({ studentId, enrollments, onUpdate, branchId, readOnly = false }) => {
  const [modalOpen, setModalOpen]         = useState(false);
  const [loading, setLoading]             = useState(false);

  const [terms, setTerms]             = useState([]);
  const [classrooms, setClassrooms]   = useState([]);
  const [campaigns, setCampaigns]     = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  const [selectedTerm, setSelectedTerm]         = useState(null);
  const [selectedClassroom, setSelectedClassroom] = useState(null);
  const [selectedDay, setSelectedDay]           = useState(null);
  const [selectedTime, setSelectedTime]         = useState(null);
  const [selectedType, setSelectedType]         = useState('regular');
  const [selectedDate, setSelectedDate]         = useState(null);
  const [startDateMode, setStartDateMode]       = useState('today');

  const [changeDayModal, setChangeDayModal]         = useState(false);
  const [changingSlot, setChangingSlot]             = useState(null);
  const [changingTerm, setChangingTerm]             = useState(null);
  const [newDay, setNewDay]                         = useState(null);
  const [newTime, setNewTime]                       = useState(null);
  const [newClassroom, setNewClassroom]             = useState(null);

  const [discountModal, setDiscountModal]           = useState(false);
  const [discountEnrollment, setDiscountEnrollment] = useState(null);
  const [discountSlot, setDiscountSlot]             = useState(null);
  const [discountList, setDiscountList]             = useState([]);
  const [discountCampaign, setDiscountCampaign]     = useState(null);
  const [discountMode, setDiscountMode]             = useState('from_today'); // 'from_term' | 'from_today' | 'from_custom'
  const [discountCustomDate, setDiscountCustomDate] = useState(null);
  const [discountLoading, setDiscountLoading]       = useState(false);

  // Yeni Kayıt modalındaki saatler — gerçek derslerden geliyor.
  const [timeSlots, setTimeSlots]         = useState([]);
  const [slotsLoading, setSlotsLoading]   = useState(false);
  // Dönemde o güne denk gelen toplam hafta sayısı — "10 haftanın 3'ünde var"
  // uyarısı için gerekli.
  const [toplamHafta, setToplamHafta]     = useState(0);
  // Doluluk tabloları (Kayıt Ekle ve Gün Değiştir ayrı ayrı — ikisi farklı
  // derslik/dönem seçiyor olabilir).
  const [kayitDoluluk, setKayitDoluluk]               = useState(null);
  const [kayitDolulukLoading, setKayitDolulukLoading] = useState(false);
  const [gunDoluluk, setGunDoluluk]                   = useState(null);
  const [gunDolulukLoading, setGunDolulukLoading]     = useState(false);

  // Gün Değiştir modalındaki saatler
  const [changeDaySlots, setChangeDaySlots]       = useState([]);
  const [changeDayToplamHafta, setChangeDayToplamHafta] = useState(0);
  const [changeDaySlotsLoading, setChangeDaySlotsLoading] = useState(false);

  // Derslik + gün/tarih değişince listeyi tazele. Bağımlılıkların HEPSİ
  // dizide: biri unutulursa liste bir önceki seçime ait kalır ve kullanıcı
  // yanlış saati seçer.
  useEffect(() => {
    let iptal = false;

    const tarih = selectedType === 'regular' ? null : selectedDate?.format('YYYY-MM-DD');
    const gun   = selectedType === 'regular' ? selectedDay : null;

    if (!selectedClassroom || !selectedTerm || (!gun && !tarih)) {
      setTimeSlots([]);
      return;
    }

    setSlotsLoading(true);
    fetchSlots({ classroomId: selectedClassroom, termId: selectedTerm, dayOfWeek: gun, date: tarih })
      .then(d => {
        if (iptal) return;
        const gelen = d.slots || [];
        setTimeSlots(gelen);
        setToplamHafta(d.total_weeks || 0);
        // Seçili saat yeni listede yoksa TEMİZLE. Aksi halde kullanıcı
        // Pazartesi için 14:30 seçip güne/dersliğe geçtiğinde seçim üstte
        // duruyor, form geçerli görünüyor ve ekranda hiç görünmeyen bir saat
        // gönderiliyor — bu değişikliğin önlemek istediği durumun ta kendisi.
        setSelectedTime(onceki => (onceki && gelen.some(s => s.start_time === onceki) ? onceki : null));
      })
      .catch(() => { if (!iptal) { setTimeSlots([]); setSelectedTime(null); } })
      .finally(() => { if (!iptal) setSlotsLoading(false); });

    return () => { iptal = true; };
  }, [selectedClassroom, selectedTerm, selectedDay, selectedDate, selectedType]);

  /**
   * Doluluk ızgarasını çeker. Derslik + dönem yeterli; gün/saat seçilmemiş
   * olsa da tablo dolu gelir — zaten kararı VERDİRMEK için orada.
   */
  const dolulukGetir = useCallback(async (classroomId, termId, setVeri, setLoading) => {
    if (!classroomId || !termId) { setVeri(null); return; }
    setLoading(true);
    try {
      const { data } = await api.get('/schedule/classroom-load', {
        params: { classroom_id: classroomId, term_id: termId },
      });
      setVeri(data);
    } catch {
      // Sessiz: tablo bir kolaylık, yüklenemezse form çalışmaya devam etmeli.
      setVeri(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    dolulukGetir(selectedClassroom, selectedTerm, setKayitDoluluk, setKayitDolulukLoading);
  }, [selectedClassroom, selectedTerm, dolulukGetir]);

  useEffect(() => {
    dolulukGetir(newClassroom, changingTerm?.id, setGunDoluluk, setGunDolulukLoading);
  }, [newClassroom, changingTerm, dolulukGetir]);

  useEffect(() => {
    let iptal = false;

    if (!newClassroom || !changingTerm?.id || !newDay) {
      setChangeDaySlots([]);
      return;
    }

    setChangeDaySlotsLoading(true);
    fetchSlots({
      classroomId: newClassroom,
      termId:      changingTerm.id,
      dayOfWeek:   newDay,
      startDate:   dayjs().format('YYYY-MM-DD'),
    })
      .then(d => {
        if (iptal) return;
        const gelen = d.slots || [];
        setChangeDaySlots(gelen);
        setChangeDayToplamHafta(d.total_weeks || 0);
        // Bkz. Yeni Kayıt — hedef gün/derslikte olmayan bir saat seçili kalmasın.
        setNewTime(onceki => (onceki && gelen.some(s => s.start_time === onceki) ? onceki : null));
      })
      .catch(() => { if (!iptal) { setChangeDaySlots([]); setNewTime(null); } })
      .finally(() => { if (!iptal) setChangeDaySlotsLoading(false); });

    return () => { iptal = true; };
  }, [newClassroom, changingTerm, newDay]);

  /**
   * Saat seçeneğinin etiketi.
   *
   * DOLULUK GÖSTERİLMİYOR (kullanıcının kararı, 2026-08-14). Gösterilebilecek
   * tek sayı TEK bir referans haftanın doluluğuydu; haftalık kayıt dönem
   * boyunca sürdüğü için ilerleyen haftalarda telafi/tek seferlik ders
   * eklendikçe o sayı gerçeği yansıtmıyor. Yanlış bir sayı, hiç sayı
   * olmamasından kötü.
   *
   * Kapasite güvenliği kaybolmuyor: kayıt sırasında firstFullDateForSlot()
   * dönem sonuna kadar HER haftayı ayrı ayrı kontrol ediyor ve dolu olan
   * haftanın tarihini söylüyor.
   *
   * `weeks` KALIYOR çünkü o dalgalanan bir değer değil, takvimin kendisi:
   * tatil yüzünden 10 haftanın yalnızca 3'ünde açık olan bir slot, her hafta
   * açık olanla aynı görünmemeli — haftalık kayıtta bu, öğrencinin
   * beklenenden az ders alması demek.
   */
  /**
   * Saat listesindeki doluluk eki: "· 2 kişi (%20)".
   *
   * Tabloyu açmadan doğrudan saate giden kullanıcı için. Veri zaten
   * yüklenmiş ızgaradan okunuyor — ayrı istek YOK.
   */
  const dolulukEtiketi = (veri, isoGun, saat) => {
    if (!veri?.days?.length || !isoGun) return '';
    const tarih = veri.days[isoGun - 1];
    const hucre = veri.slots?.find(s => s.start_time === saat)?.days?.[tarih];
    if (hucre == null) return '';

    const kontenjan = veri.capacity || 0;
    const yuzde = kontenjan ? Math.round((hucre.regular / kontenjan) * 100) : 0;
    return ` · ${hucre.regular} kişi (%${yuzde})${hucre.full_from ? ' ⚠' : ''}`;
  };

  const slotEtiketi = (s, toplamHafta = 0) => {
    const eksikHafta = toplamHafta > 0 && s.weeks > 0 && s.weeks < toplamHafta
      ? ` · ${toplamHafta} haftanın ${s.weeks}'inde var`
      : '';

    return `${s.start_time} — ${s.end_time}${eksikHafta}`;
  };

  useEffect(() => { if (branchId && !readOnly) fetchData(); }, [branchId, readOnly]);

  const fetchData = async () => {
    try {
      const [termsRes, classroomsRes, campaignsRes] = await Promise.all([
        getTerms({ per_page: 50, is_active: 1, branch_id: branchId }),
        // exclude_archive: "Geçmiş Dönem" derslikleri geçmiş kayıt arşivi —
        // oraya yeni öğrenci kaydı açılmamalı. Üstelik o dersliklerde 7.000+
        // seans olduğu için "bu saatte ders var mı" kapısı orada GEÇİYOR,
        // yani yanlışlıkla seçilirse hiçbir uyarı çıkmadan kayıt açılıyordu.
        getClassrooms({ per_page: 100, branch_id: branchId, exclude_archive: 1 }),
        getCampaigns({ per_page: 100, branch_id: branchId, is_active: 1 }),
      ]);
      setTerms(termsRes.data.data || []);
      setClassrooms(classroomsRes.data.data || []);
      setCampaigns(campaignsRes.data.data || []);
    } catch {
      message.error('Veriler yüklenemedi.');
    }
  };

  const resetModal = () => {
    setSelectedTerm(null);
    setSelectedClassroom(null);
    setSelectedDay(null);
    setSelectedTime(null);
    setSelectedType('regular');
    setSelectedDate(null);
    setSelectedCampaign(null);
  };

  // Group enrollments by term
  const groupedList = enrollments.reduce((acc, te) => {
    const existing = acc.find(x => x.term?.id === te.term?.id);
    if (existing) {
      existing.slotEnrollments = [
        ...(existing.slotEnrollments || []),
        ...(te.slot_enrollments || []),
      ];
    } else {
      acc.push({ ...te, slotEnrollments: te.slot_enrollments || [] });
    }
    return acc;
  }, []);

  const isFormValid = () => {
    if (!selectedTerm || !selectedClassroom || !selectedTime) return false;
    if (selectedType === 'regular') return !!selectedDay;
    if (selectedType === 'one_time') return !!selectedDate;
    return !!selectedDate;
  };

  /**
   * "Bu saatte ders yok" uyarısını kullanıcıya sorar.
   *
   * Sunucu 409 + confirm_required döndüğünde çağrılıyor; onaylanırsa aynı
   * istek `confirm: true` ile tekrarlanıyor. Tatil ekleme akışıyla AYNI
   * sözleşme (bkz. TermsTab.handleAddHoliday). Öğretmende bu yol hiç
   * açılmaz — sunucu ona 409 değil 422 döner.
   */
  const dersYokOnayi = (mesaj) => new Promise((resolve) => {
    Modal.confirm({
      title: 'Bu saatte ders kaydı yok',
      content: mesaj,
      okText: 'Yine de ekle',
      cancelText: 'Vazgeç',
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });

  const dersYokMu = (err) =>
    err?.response?.status === 409 && err?.response?.data?.confirm_required;

  const handleEnroll = async (confirm = false) => {
    if (!isFormValid()) { message.error('Lütfen tüm alanları doldurunuz.'); return; }

    const [hours, minutes] = selectedTime.split(':').map(Number);
    const endTime = `${String(hours + 1).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

    setLoading(true);
    try {
      const enrollRes = await api.post('/schedule/enroll', {
        confirm: confirm || undefined,
        student_id:   parseInt(studentId),
        term_id:      selectedTerm,
        classroom_id: selectedClassroom,
        day_of_week:  selectedType === 'regular' ? selectedDay : (selectedDate?.isoWeekday() ?? selectedDay),
        start_time:   selectedTime,
        end_time:     endTime,
        type:         selectedType,
        start_date:   selectedType === 'regular'
          ? (startDateMode === 'term_start' ? undefined : startDateMode === 'custom' ? selectedDate?.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'))
          : (selectedDate ? selectedDate.format('YYYY-MM-DD') : undefined),
      });

      if (selectedCampaign) {
        const termEnrollmentId = enrollRes.data.term_enrollment_id;
        // İNDİRİM AÇILAN SATIRA YAZILIR, DÖNEME DEĞİL.
        //
        // Eskiden `slot_enrollment_id` gönderilmiyordu, yani indirim TÜM
        // DÖNEME yazılıyordu ve `AttendanceChargeService` onu o dönemdeki
        // HER slota uyguluyordu — sonradan açılanlar dahil. Eren Bey'in üç
        // ayrı şikâyeti de bundan doğuyordu (17.10.2026): "2. kayda
        // kendiliğinden uygulanıyor", "ben de seçince çift oluyor",
        // "hiçbir şey seçmedim yine indirim var".
        //
        // Ölçüm: canlıda 154 indirim tüm döneme, 187'si slota yazılmış —
        // yani formdan girilen her indirim bu yoldan geçiyordu.
        await api.post(`/enrollments/${termEnrollmentId}/discounts`, {
          campaign_id: selectedCampaign,
          type: 'percentage',
          value: 0,
          slot_enrollment_id: enrollRes.data.slot_enrollment_id,
        });
      }

      message.success('Kayıt oluşturuldu.');
      setModalOpen(false);
      resetModal();
      onUpdate();
    } catch (err) {
      if (!confirm && dersYokMu(err)) {
        setLoading(false);
        if (await dersYokOnayi(err.response.data.message)) {
          return handleEnroll(true);
        }
        return;
      }
      message.error(err?.response?.data?.message || 'Kayıt oluşturulamadı.');
    } finally {
      setLoading(false);
    }
  };

  const handleChangeDay = async (confirm = false) => {
    if (!newDay || !newTime || !newClassroom || !changingSlot) return;
    const [hours, minutes] = newTime.split(':').map(Number);
    const endTime = `${String(hours + 1).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    setLoading(true);
    try {
      await api.post('/schedule/change-day', {
        slot_enrollment_id: changingSlot.id,
        new_day_of_week:    newDay,
        new_start_time:     newTime,
        new_end_time:       endTime,
        new_classroom_id:   newClassroom,
        change_date:        dayjs().format('YYYY-MM-DD'),
        confirm:            confirm || undefined,
      });
      message.success('Ders günü değiştirildi.');
      setChangeDayModal(false);
      setChangingSlot(null);
      onUpdate();
    } catch (err) {
      if (!confirm && dersYokMu(err)) {
        setLoading(false);
        if (await dersYokOnayi(err.response.data.message)) {
          return handleChangeDay(true);
        }
        return;
      }
      message.error(err?.response?.data?.message || 'Gün değiştirilemedi.');
    } finally {
      setLoading(false);
    }
  };

  const openDiscountModal = async (termEnrollment, slot) => {
    setDiscountEnrollment(termEnrollment);
    setDiscountSlot(slot);
    setDiscountCampaign(null);
    setDiscountMode('from_today');
    setDiscountCustomDate(null);
    setDiscountModal(true);
    try {
      const res = await api.get(`/enrollments/${termEnrollment.id}/discounts`);
      const all = res.data || [];
      // Bu slota özel veya tüm dönem indirimleri (slot_enrollment_id null)
      setDiscountList(all.filter(d => d.slot_enrollment_id == slot.id || d.slot_enrollment_id === null));
    } catch {
      setDiscountList([]);
    }
  };

  const refreshDiscountList = async () => {
    try {
      const res = await api.get(`/enrollments/${discountEnrollment.id}/discounts`);
      const all = res.data || [];
      setDiscountList(all.filter(d => d.slot_enrollment_id == discountSlot.id || d.slot_enrollment_id === null));
    } catch {
      setDiscountList([]);
    }
  };

  const handleSaveDiscount = async () => {
    if (!discountCampaign || !discountSlot) return;
    setDiscountLoading(true);
    const campaign = campaigns.find(c => c.id === discountCampaign);
    const termStartDate = discountEnrollment?.term?.start_date;
    let appliesFrom;
    if (discountMode === 'from_term' && termStartDate) {
      appliesFrom = dayjs(termStartDate).format('YYYY-MM-DD');
    } else if (discountMode === 'from_custom' && discountCustomDate) {
      appliesFrom = discountCustomDate.format('YYYY-MM-DD');
    } else {
      appliesFrom = dayjs().format('YYYY-MM-DD');
    }
    try {
      // Mevcut slot-specific indirimi sil
      const existing = discountList.find(d => d.slot_enrollment_id == discountSlot.id);
      if (existing) await api.delete(`/discounts/${existing.id}`);

      await api.post(`/enrollments/${discountEnrollment.id}/discounts`, {
        campaign_id:        discountCampaign,
        slot_enrollment_id: discountSlot.id,
        type:               campaign?.type || 'percentage',
        value:              campaign?.value ?? 0,
        applies_from:       appliesFrom,
      });
      message.success('İndirim kaydedildi.');
      setDiscountCampaign(null);
      await refreshDiscountList();
      onUpdate();
    } catch (err) {
      message.error(err?.response?.data?.message || 'İndirim kaydedilemedi.');
    } finally {
      setDiscountLoading(false);
    }
  };

  const handleRemoveDiscount = async (discountId) => {
    try {
      await api.delete(`/discounts/${discountId}`);
      message.success('İndirim silindi.');
      await refreshDiscountList();
      onUpdate();
    } catch {
      message.error('İndirim silinemedi.');
    }
  };

  const handleCancelSlot = async (slot) => {
    try {
      await api.post('/schedule/cancel-enrollment', {
        slot_enrollment_id: slot.id,
        cancel_date: dayjs().format('YYYY-MM-DD'),
      });
      message.success('Kayıt iptal edildi.');
      onUpdate();
    } catch {
      message.error('Kayıt iptal edilemedi.');
    }
  };

  return (
    <div>
      {!readOnly && (
        <div className="flex justify-end mb-4">
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { resetModal(); setModalOpen(true); }}>
            Yeni Kayıt
          </Button>
        </div>
      )}

      {groupedList.length === 0 ? (
        <Card><p className="text-gray-400 text-center">Henüz kayıt yok.</p></Card>
      ) : (
        groupedList.map(termEnrollment => (
          <Card
            key={termEnrollment.term?.id}
            className="mb-4"
            title={
              <div className="flex justify-between items-center">
                <span>{termEnrollment.term?.name}</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-400">
                    {dayjs(termEnrollment.term?.start_date).format('DD.MM.YYYY')} — {dayjs(termEnrollment.term?.end_date).format('DD.MM.YYYY')}
                  </span>
                  {/* İKİ AYRI DURUM, İKİ AYRI ETİKET.
                      Burada tek bir "Aktif/Pasif" etiketi vardı ve KAYDIN
                      durumunu gösteriyordu; ama kart başlığında dönem adı ve
                      dönem tarihleriyle yan yana durduğu için "bu dönem
                      aktif" diye okunuyordu. Pasife alınmış 2026 Yaz dönemi
                      "Aktif" görünüyordu (2026-09-07, canlıda bildirildi).

                      Dönem durumu her zaman yazılıyor; kayıt durumu ise
                      yalnızca olağan dışıysa (iptal/pasif) — normal kayıtta
                      ikinci bir etiket gürültü olurdu. */}
                  <Tag color={termEnrollment.term?.is_active ? 'green' : 'orange'}>
                    {termEnrollment.term?.is_active ? 'Dönem Aktif' : 'Dönem Pasif'}
                  </Tag>
                  {termEnrollment.is_deleted && <Tag color="default">Kayıt İptal Edildi</Tag>}
                  {!termEnrollment.is_deleted && !termEnrollment.is_active && (
                    <Tag color="red">Kayıt Pasif</Tag>
                  )}
                </div>
              </div>
            }
          >
            {termEnrollment.slotEnrollments.length === 0 ? (
              <p className="text-gray-400 text-sm">Bu dönem için slot yok.</p>
            ) : (
              termEnrollment.slotEnrollments.map(slot => {
                const allDiscounts = (termEnrollment.discounts || []).filter(d => d.is_active !== false);
                const slotDiscount = allDiscounts.find(d => d.slot_enrollment_id == slot.id) || null;
                const termWideDiscount = !slotDiscount ? (allDiscounts.find(d => d.slot_enrollment_id === null) || null) : null;
                const effectiveDiscount = slotDiscount || termWideDiscount;

                const discountLabel = effectiveDiscount ? (() => {
                  const name = effectiveDiscount.campaign?.name || 'İndirim';
                  const val  = effectiveDiscount.type === 'percentage' ? `%${parseFloat(effectiveDiscount.value)}` : `${effectiveDiscount.value} ₺`;
                  const scope = slotDiscount ? 'Bu slota özel' : 'Tüm dönem';
                  const when  = effectiveDiscount.applies_from
                    ? `${dayjs(effectiveDiscount.applies_from).format('DD.MM.YYYY')} itibaren`
                    : 'Dönem başından';
                  return `${name} ${val} · ${scope} · ${when}`;
                })() : null;

                return (
                <div key={slot.id} className="border rounded mb-2">
                  <div className="flex justify-between items-center p-3">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <strong>{slot.classroom?.name}</strong>
                        <span className="text-gray-500">
                          {slotWhenLabel(slot)} {slot.start_time?.substring(0, 5)} — {slot.end_time?.substring(0, 5)}
                        </span>
                        <Tag color={TYPE_COLORS[slot.type] || 'blue'}>
                          {TYPE_LABELS[slot.type] || slot.type}
                        </Tag>
                        {!slot.is_active && <Tag color="red">İptal Edildi</Tag>}
                      </div>
                      {effectiveDiscount ? (
                        <span className="text-xs text-purple-700 pl-0.5">{discountLabel}</span>
                      ) : slot.is_active ? (
                        <span className="text-xs text-green-600 pl-0.5">Ücretli — indirim tanımlı değil</span>
                      ) : null}
                    </div>
                    {!readOnly && <div className="flex gap-2 shrink-0">
                      {slot.type === 'regular' && slot.is_active && (
                        <Button icon={<SwapOutlined />} size="small" onClick={() => {
                          setChangingSlot(slot);
                          setChangingTerm(termEnrollment.term);
                          setNewDay(slot.day_of_week);
                          setNewTime(slot.start_time?.substring(0, 5));
                          setNewClassroom(slot.classroom_id);
                          setChangeDayModal(true);
                        }}>
                          Gün Değiştir
                        </Button>
                      )}
                      <Button size="small" icon={<PercentageOutlined />} onClick={() => openDiscountModal(termEnrollment, slot)}>
                        {effectiveDiscount ? 'İndirimi Düzenle' : 'İndirim Ekle'}
                      </Button>
                      {slot.is_active && (
                        <Popconfirm
                          title="Bu slot kaydı iptal edilsin mi?"
                          onConfirm={() => handleCancelSlot(slot)}
                          okText="Evet"
                          cancelText="Hayır"
                        >
                          <Button danger size="small">İptal Et</Button>
                        </Popconfirm>
                      )}
                    </div>}
                  </div>
                </div>
                );
              })
            )}
          </Card>
        ))
      )}

      {/* Yeni Kayıt Modal */}
      <Modal
        title="Yeni Slot Kaydı"
        open={modalOpen}
        onCancel={() => { setModalOpen(false); resetModal(); }}
        footer={null}
        width={640}
      >
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Dönem</label>
          <Select style={{ width: '100%' }} placeholder="Dönem seçiniz" value={selectedTerm} onChange={setSelectedTerm}>
            {terms.map(t => (
              <Option key={t.id} value={t.id}>
                {t.name} ({dayjs(t.start_date).format('DD.MM.YYYY')} — {dayjs(t.end_date).format('DD.MM.YYYY')})
              </Option>
            ))}
          </Select>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Kayıt Tipi</label>
          <Select style={{ width: '100%' }} value={selectedType} onChange={v => { setSelectedType(v); setSelectedDay(null); setSelectedDate(null); }}>
            <Option value="regular">Normal — Her hafta bu güne eklenir</Option>
            <Option value="one_time">Tek Seferlik — Sadece o hafta</Option>
            <Option value="trial">Deneme — Sadece tek ders</Option>
            <Option value="makeup">Telafi — Sadece tek ders</Option>
            <Option value="makeup_uncontrolled">Kontrolsüz Telafi — Geçmiş dönem hakkı</Option>
          </Select>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Derslik</label>
          <Select style={{ width: '100%' }} placeholder="Derslik seçiniz" value={selectedClassroom} onChange={setSelectedClassroom}>
            {classrooms.map(c => <Option key={c.id} value={c.id}>{c.name} (max {c.capacity} kişi)</Option>)}
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1">{selectedType === 'regular' ? 'Gün' : 'Tarih'}</label>
            {selectedType === 'regular' ? (
              <Select style={{ width: '100%' }} placeholder="Gün seçiniz" value={selectedDay} onChange={setSelectedDay}>
                {DAY_NAMES.slice(1, 7).map((day, i) => (
                  <Option key={i + 1} value={i + 1}>{day}</Option>
                ))}
              </Select>
            ) : (
              <DatePicker
                style={{ width: '100%' }}
                format="DD.MM.YYYY"
                value={selectedDate}
                onChange={d => { setSelectedDate(d); if (d) setSelectedDay(d.isoWeekday()); }}
              />
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Saat</label>
            {/* Saatler dönemin eski tek aralığından DEĞİL, o derslikte
                gerçekten açılmış derslerden geliyor. Liste boşsa henüz
                toplu ders üretilmemiş demektir. */}
            <Select
              style={{ width: '100%' }}
              placeholder={slotsLoading ? 'Yükleniyor...' : 'Saat seçiniz'}
              value={selectedTime}
              onChange={setSelectedTime}
              loading={slotsLoading}
              notFoundContent={
                slotsLoading ? 'Yükleniyor...' : 'Bu gün için ders kaydı yok.'
              }
            >
              {timeSlots.map(s => (
                <Option key={s.start_time} value={s.start_time}>
                  {slotEtiketi(s, toplamHafta)}{dolulukEtiketi(kayitDoluluk, selectedDay, s.start_time)}
                </Option>
              ))}
            </Select>
            {/* Üretim hiç çalışmamışsa liste boş gelir. Kullanıcıya ne
                yapması gerektiğini söylemezsek "saat seçemiyorum" diye
                takılıp kalıyor. */}
            {!slotsLoading && selectedClassroom && (selectedDay || selectedDate) && timeSlots.length === 0 && (
              <div className="mt-1 text-xs text-orange-600">
                Bu derslikte seçilen gün için ders kaydı bulunmuyor. Önce
                Dönemler ekranından <strong>Toplu Ders Oluştur</strong> ile ders açın.
              </div>
            )}
          </div>
        </div>

        {/* Doluluk tablosu: hangi gün/saat şişkin, hangisi boş. Hücreye
            tıklayınca gün ve saat yukarıdaki alanlara doluyor — Eren Bey
            veli telefondayken rapora gitmek zorunda kalmıyor. */}
        <ClassroomLoadPanel
          veri={kayitDoluluk}
          loading={kayitDolulukLoading}
          seciliGun={selectedDay}
          seciliSaat={selectedTime}
          onSelect={(gun, saat) => {
            // Tek seferlik/deneme/telafi kaydında gün TARİHTEN türüyor;
            // tabloya tıklayınca o günün ilk uygun tarihini seçmek yerine
            // yalnızca saati alıyoruz, tarihi kullanıcı zaten seçmiş oluyor.
            if (selectedType === 'regular') setSelectedDay(gun);
            setSelectedTime(saat);
          }}
        />

        {selectedType === 'regular' && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Başlangıç</label>
            <Radio.Group value={startDateMode} onChange={e => { setStartDateMode(e.target.value); setSelectedDate(null); }}>
              <Radio value="today">Bugünden itibaren</Radio>
              <Radio value="term_start">Dönem başından</Radio>
              <Radio value="custom">Özel tarih</Radio>
            </Radio.Group>
            {startDateMode === 'custom' && (
              <DatePicker
                style={{ width: '100%', marginTop: 8 }}
                format="DD.MM.YYYY"
                value={selectedDate}
                onChange={d => setSelectedDate(d)}
                placeholder="Başlangıç tarihi seçin"
              />
            )}
          </div>
        )}

        {/*
          KAMPANYA SEÇİCİ ARTIK HER ZAMAN GÖRÜNÜYOR (2026-08-19).

          Önceden "öğrencinin bu dönemde zaten kaydı varsa" gizleniyordu.
          Gizlenme sebebi mantıklıydı — kayıt formundan seçilen indirim
          `slot_enrollment_id` OLMADAN, yani TÜM DÖNEME yazılıyor
          (handleEnroll) ve o indirim yeni açılan slota da kendiliğinden
          uygulanıyor (AttendanceChargeService: slot_enrollment_id IS NULL
          OR = slot). Yani ikinci kayıtta tekrar seçmeye gerek yok.

          Ama ekran bunu SÖYLEMİYORDU: seçici sessizce kayboluyor, sonra
          yeni kaydın satırında "Kardeş İndirimi · Tüm dönem" beliriyordu.
          Kullanıcıya iki ayrı arıza gibi göründü ("indirim seçeneği
          gelmiyor" + "girmediğim indirim geliyor") — ikisi de buydu.

          Çözüm davranışı değil GÖRÜNÜRLÜĞÜ değiştiriyor: para hesabı aynı,
          mevcut indirim açıkça yazılıyor ve seçici erişilebilir kalıyor.
        */}
        {(() => {
          if (campaigns.length === 0) return null;

          // Dönem indirimleri `enrollments` içinde zaten geliyor (slot
          // satırındaki etiket de oradan besleniyor) — ek istek yok.
          const donemIndirimleri = enrollments
            .filter(e => e.term?.id === selectedTerm && !e.is_deleted)
            .flatMap(e => e.discounts || [])
            .filter(d => d.is_active !== false && d.slot_enrollment_id === null);

          return (
            <div className="mb-4">
              {donemIndirimleri.length > 0 && (
                <div className="mb-2 p-3 bg-orange-50 border border-orange-200 rounded">
                  <p className="text-xs font-semibold text-orange-500 uppercase mb-1">Dikkat — eski usul indirim</p>
                  {donemIndirimleri.map(d => (
                    <div key={d.id} className="text-sm font-medium text-orange-900">
                      {d.campaign?.name || 'İndirim'} — {d.type === 'percentage' ? `%${parseFloat(d.value)}` : `${d.value} ₺`}
                    </div>
                  ))}
                  <p className="text-xs text-orange-700 mt-1">
                    Bu indirim <strong>tüm döneme</strong> tanımlı, yani bu yeni kayda da uygulanacak.
                    Artık bir derse yalnızca <strong>bir</strong> indirim uygulanabildiği için aşağıdan
                    kampanya seçerseniz kayıt <strong>reddedilir</strong> — önce bu indirimi kaldırmanız gerekir.
                  </p>
                </div>
              )}
              <label className="block text-sm font-medium mb-1">Kampanya / İndirim <span className="text-gray-400 font-normal">(isteğe bağlı)</span></label>
              <Select
                style={{ width: '100%' }}
                placeholder="Kampanya seçiniz"
                value={selectedCampaign}
                onChange={setSelectedCampaign}
                allowClear
              >
                {campaigns.map(c => (
                  <Option key={c.id} value={c.id}>
                    {c.name} — {c.type === 'percentage' ? `%${c.value}` : `${c.value} ₺`}
                  </Option>
                ))}
              </Select>
            </div>
          );
        })()}

        <div className="flex justify-end gap-2">
          <Button onClick={() => { setModalOpen(false); resetModal(); }}>İptal</Button>
          {/* onClick={handleEnroll} YAZMAYIN: React tıklama olayını ilk
              argüman olarak geçirir, `confirm` bir Event nesnesi olur ve
              sunucu "confirm alanı doğru veya yanlış olmalıdır" diyip 422
              döner — yani kayıt hiç açılamaz. Sarmalayıcı şart. */}
          <Button type="primary" loading={loading} disabled={!isFormValid()} onClick={() => handleEnroll()}>
            Kaydet
          </Button>
        </div>
      </Modal>

      {/* Gün Değiştirme Modal */}
      <Modal
        title="Ders Günü Değiştir"
        open={changeDayModal}
        onCancel={() => setChangeDayModal(false)}
        footer={null}
        width={640}
      >
        <p className="text-gray-500 mb-4 text-sm">
          Bugünden itibaren eski ders silinecek, yeni güne dönem sonuna kadar ders eklenecek.
        </p>

        {/* Veli "günümüzü değiştirelim" diye aradığında en çok burası
            lazım: hangi gün boş, tabloya bakıp konuşuluyor. */}
        <ClassroomLoadPanel
          veri={gunDoluluk}
          loading={gunDolulukLoading}
          seciliGun={newDay}
          seciliSaat={newTime}
          onSelect={(gun, saat) => { setNewDay(gun); setNewTime(saat); }}
        />

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Yeni Gün</label>
          <Select style={{ width: '100%' }} value={newDay} onChange={setNewDay}>
            {DAY_NAMES.slice(1, 7).map((day, i) => (
              <Option key={i + 1} value={i + 1}>{day}</Option>
            ))}
          </Select>
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Yeni Saat</label>
          <Select
            style={{ width: '100%' }}
            value={newTime}
            onChange={setNewTime}
            loading={changeDaySlotsLoading}
            notFoundContent={changeDaySlotsLoading ? 'Yükleniyor...' : 'Bu gün için ders kaydı yok.'}
          >
            {changeDaySlots.map(s => (
              <Option key={s.start_time} value={s.start_time}>{slotEtiketi(s, changeDayToplamHafta)}{dolulukEtiketi(gunDoluluk, newDay, s.start_time)}</Option>
            ))}
          </Select>
          {!changeDaySlotsLoading && newClassroom && newDay && changeDaySlots.length === 0 && (
            <div className="mt-1 text-xs text-orange-600">
              Bu derslikte seçilen gün için ders kaydı bulunmuyor.
            </div>
          )}
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Yeni Derslik</label>
          <Select style={{ width: '100%' }} value={newClassroom} onChange={setNewClassroom}>
            {classrooms.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
          </Select>
        </div>
        <div className="flex justify-end gap-2">
          <Button onClick={() => setChangeDayModal(false)}>İptal</Button>
          {/* Bkz. Yeni Kayıt butonu — olay nesnesi `confirm` alanına düşmesin. */}
          <Button type="primary" loading={loading} onClick={() => handleChangeDay()}>Değiştir</Button>
        </div>
      </Modal>

      {/* İndirim Modal */}
      <Modal
        title={`${DAY_NAMES[discountSlot?.day_of_week] || ''} ${discountSlot?.start_time?.substring(0,5) || ''} — İndirim`}
        open={discountModal}
        onCancel={() => { setDiscountModal(false); setDiscountEnrollment(null); setDiscountSlot(null); setDiscountList([]); setDiscountCampaign(null); }}
        footer={null}
        width={460}
        destroyOnClose
      >
        {(() => {
          const slotDiscount = discountList.find(d => d.slot_enrollment_id == discountSlot?.id) || null;
          const termWideList = discountList.filter(d => d.slot_enrollment_id === null);
          return (
            <>
              {/* Mevcut slot indirimi */}
              {slotDiscount && (
                <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-xs font-semibold text-purple-500 uppercase mb-1">Bu Slota Özel İndirim</p>
                      <p className="font-medium text-purple-900">{slotDiscount.campaign?.name || 'İndirim'} — {slotDiscount.type === 'percentage' ? `%${parseFloat(slotDiscount.value)}` : `${slotDiscount.value} ₺`}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {slotDiscount.applies_from ? dayjs(slotDiscount.applies_from).format('DD.MM.YYYY') + ' tarihinden itibaren' : 'Dönem başından itibaren'}
                      </p>
                    </div>
                    <Popconfirm title="İndirim silinsin mi?" onConfirm={() => handleRemoveDiscount(slotDiscount.id)} okText="Evet" cancelText="Hayır">
                      <Button danger size="small" icon={<DeleteOutlined />}>Sil</Button>
                    </Popconfirm>
                  </div>
                </div>
              )}

              {/* Term-wide indirimler */}
              {termWideList.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs font-semibold text-orange-500 uppercase mb-2">Tüm Döneme Uygulanan</p>
                  {termWideList.map(d => (
                    <div key={d.id} className="flex justify-between items-center p-2 bg-orange-50 border border-orange-200 rounded mb-2">
                      <div>
                        <span className="font-medium text-orange-800">{d.campaign?.name || 'İndirim'}</span>
                        <span className="ml-2 text-orange-600 font-semibold">{d.type === 'percentage' ? `%${parseFloat(d.value)}` : `${d.value} ₺`}</span>
                        <div className="text-xs text-gray-400 mt-0.5">
                          {d.applies_from ? dayjs(d.applies_from).format('DD.MM.YYYY') + ' itibaren' : 'Dönem başından'} · Tüm slotlara uygulanıyor
                        </div>
                      </div>
                      <Popconfirm title="İndirim silinsin mi?" onConfirm={() => handleRemoveDiscount(d.id)} okText="Evet" cancelText="Hayır">
                        <Button danger size="small" icon={<DeleteOutlined />} />
                      </Popconfirm>
                    </div>
                  ))}
                </div>
              )}

              {!slotDiscount && termWideList.length === 0 && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded text-sm text-green-700">
                  İndirim tanımlı değil — <strong>ücretli</strong>
                </div>
              )}

              <div className="border-t pt-4">
                {/*
                  KAPI SLOT İNDİRİMİNE DEĞİL, BU DERSE ETKİ EDEN HER İNDİRİME
                  BAKAR.

                  Eskiden yalnızca `slotDiscount` kontrol ediliyordu; TÜM
                  DÖNEM indirimi varken ekleme formu açık kalıyordu. Kullanıcı
                  ekliyor, `AttendanceChargeService` ikisini de uyguluyor ve
                  ücret sessizce düşüyordu. Canlıda ölçüldü (17.10.2026):
                  6 öğrencide aynı "Kardeş İndirimi 75 ₺" iki/üç kez tanımlı,
                  ücret 625 yerine 550.

                  Backend de aynı kuralı uyguluyor (DiscountController::
                  cakisanIndirim, 422). Burası ikinci katman — arayüzde
                  engellemek yetki değildir, ama kullanıcıyı 422'ye
                  çarptırmamak için önce burada duruyor.
                */}
                {(slotDiscount || termWideList.length > 0) ? (
                  <>
                    <div className="mb-3 p-3 bg-yellow-50 border border-yellow-300 rounded text-sm text-yellow-800">
                      Bir derse yalnızca <strong>bir</strong> indirim uygulanabilir.
                      {slotDiscount
                        ? ' Bu kayıtta zaten bir indirim var; yenisini eklemek için önce onu kaldırın.'
                        : ' Bu öğrencinin tüm dönemine tanımlı bir indirim zaten bu derse uygulanıyor;'
                          + ' yenisini eklemek için önce onu kaldırın. (Tüm döneme tanımlı olduğu için'
                          + ' kaldırmak diğer kayıtları da etkiler.)'}
                    </div>
                    <div className="flex justify-end">
                      <Button onClick={() => { setDiscountModal(false); setDiscountEnrollment(null); setDiscountSlot(null); setDiscountList([]); setDiscountCampaign(null); }}>Kapat</Button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-medium mb-2">İndirim Ekle</p>
                    <Select
                      style={{ width: '100%' }}
                      placeholder="Kampanya seçiniz"
                      value={discountCampaign}
                      onChange={setDiscountCampaign}
                      allowClear
                      className="mb-3"
                    >
                      {campaigns.map(c => (
                        <Option key={c.id} value={c.id}>
                          {c.name} — {c.type === 'percentage' ? `%${c.value}` : `${c.value} ₺`}
                        </Option>
                      ))}
                    </Select>
                    <div className="mb-4">
                      <p className="text-xs text-gray-500 mb-1">Hangi derslerden itibaren geçerli olsun?</p>
                      <Radio.Group value={discountMode} onChange={e => { setDiscountMode(e.target.value); setDiscountCustomDate(null); }}>
                        <Radio value="from_today">Bugünden itibaren</Radio>
                        <Radio value="from_term">Dönem başından itibaren (geçmişe dönük)</Radio>
                        <Radio value="from_custom">Belirli bir tarihten itibaren</Radio>
                      </Radio.Group>
                      {discountMode === 'from_custom' && (
                        <DatePicker
                          style={{ width: '100%', marginTop: 8 }}
                          value={discountCustomDate}
                          onChange={setDiscountCustomDate}
                          format="DD.MM.YYYY"
                          placeholder="Tarih seçiniz"
                          disabledDate={null}
                        />
                      )}
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button onClick={() => { setDiscountModal(false); setDiscountEnrollment(null); setDiscountSlot(null); setDiscountList([]); setDiscountCampaign(null); }}>İptal</Button>
                      <Button type="primary" loading={discountLoading} disabled={!discountCampaign || (discountMode === 'from_custom' && !discountCustomDate)} onClick={handleSaveDiscount}>
                        Kaydet
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </>
          );
        })()}
      </Modal>
    </div>
  );
};

export default StudentEnrollmentsTab;
